import * as React from 'react';
import type { DataValue } from 'react-apollo';
import { graphql } from 'react-apollo';

import gql from 'graphql-tag';

import { Typography2 } from '@coursera/cds-core';
import { Box, Checkbox } from '@coursera/coursera-ui';

import { Spacer } from 'bundles/enterprise-ui/components';
import PiiRadio from 'bundles/internal-enterprise/components/InternalThirdPartyOrganizationPage/PiiRadio';
import type {
  DataProtectionQuery,
  DataProtectionQueryVariables,
} from 'bundles/internal-enterprise/components/InternalThirdPartyOrganizationPage/__generated__/DataProtectionQuery';
import DropdownMenu from 'bundles/internal-ui/components/forms/DropdownMenu';

import _t from 'i18n!nls/internal-enterprise';

const updateGdprMode = (id: $TSFixMe, mode: $TSFixMe) =>
  fetch(`/api/gdprSettings.v1/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      gdprMode: mode,
    }),
  });

type PropsFromCaller = {
  thirdPartyOrganizationId?: string;
};

type PropsFromGraphql = {
  data?: DataValue<DataProtectionQuery, DataProtectionQueryVariables>;
};

type Props = PropsFromCaller & PropsFromGraphql;

const DataProtection: React.FC<Props> = ({ data, thirdPartyOrganizationId }) => {
  const gdprMode = data?.GdprSettingsV1Resource?.get?.gdprMode;

  return (
    <div data-testid="data-protection">
      {thirdPartyOrganizationId && (
        <React.Fragment>
          <PiiRadio thirdPartyOrganizationId={thirdPartyOrganizationId} />
          <Spacer size={32} />
        </React.Fragment>
      )}
      <Box alignItems="center">
        <Checkbox checked disabled />
        <Spacer size={12} />
        <Typography2 component="p">{_t('Coursera as a Data Controller')}</Typography2>
      </Box>
      <small>
        {_t(
          'For Q3, we are not yet supporting Data Processor mode, so for the short term, it will not be possible to turn off Data Controller mode.'
        )}
      </small>
      <Spacer size={32} />
      <DropdownMenu
        selectProps={{
          'data-e2e': 'gdpr-mode',
        }}
        label={_t('Select the Appropriate Data Controller Mode for the Customer (from least to most restrictive)')}
        onChange={(event) => {
          updateGdprMode(thirdPartyOrganizationId, event.target.value).then(() => data?.refetch());
        }}
        value={gdprMode}
        options={[
          {
            label: _t(
              'Proactive Account Linking, and Recovery Email and Password Prompts Before and After Login (default)'
            ),
            value: 'GDPR_MODE_DEFAULT',
          },
          { label: _t('Restrict Proactive Email and Password Prompts to Post-Login'), value: 'GDPR_MODE_LIGHT' },
          { label: _t('Disable All Proactive Recovery Email and Password Prompts'), value: 'GDPR_MODE_RESTRICTIVE' },
          // TODO: uncomment when strict mode is implemented
          // {
          //   label: _t('Disable Account Linking and All Proactive Recovery Email and Password Prompt'),
          //   value: 'GDPR_MODE_STRICT',
          // },
        ]}
      />
    </div>
  );
};

export const QueryForDataProtection = gql`
  query DataProtectionQuery($thirdPartyOrganizationId: String!) {
    GdprSettingsV1Resource {
      get(id: $thirdPartyOrganizationId) {
        id
        gdprMode
      }
    }
  }
`;

export default graphql<PropsFromCaller, DataProtectionQuery, DataProtectionQueryVariables, PropsFromGraphql>(
  QueryForDataProtection,
  {
    skip: (props) => !props.thirdPartyOrganizationId,
    options: (props) => ({
      variables: { thirdPartyOrganizationId: props.thirdPartyOrganizationId! },
    }),
  }
)(DataProtection);
